/**
 * File Type Configuration for Prescription Uploads
 * This configuration file defines the allowed file types for prescription uploads.
 * 
 * To add support for new file types:
 * 1. Add the MIME type as a key in ALLOWED_FILE_TYPES
 * 2. Include the file extensions in the extensions array
 * 3. Provide a user-friendly display name
 * 
 * The system will automatically:
 * - Generate validation messages
 * - Create HTML accept attributes
 * - Handle file extension detection
 * - Provide MIME type mapping
 */

export const PRESCRIPTION_FILE_CONFIG = {
  // Maximum file size in bytes (3MB)
  MAX_FILE_SIZE: 3 * 1024 * 1024,
  
  // Allowed file types configuration
  ALLOWED_FILE_TYPES: {
    "image/jpeg": { 
      extensions: ["jpg", "jpeg"], 
      displayName: "JPEG",
      category: "image"
    },
    "image/png": { 
      extensions: ["png"], 
      displayName: "PNG",
      category: "image"
    },
    "image/gif": { 
      extensions: ["gif"], 
      displayName: "GIF",
      category: "image"
    },
    "image/webp": { 
      extensions: ["webp"], 
      displayName: "WebP",
      category: "image"
    },
    "application/pdf": { 
      extensions: ["pdf"], 
      displayName: "PDF",
      category: "document"
    }
    // To add more file types, add them here following the same pattern
    // Example:
    // "image/tiff": { 
    //   extensions: ["tiff", "tif"], 
    //   displayName: "TIFF",
    //   category: "image"
    // }
  }
};

/**
 * Helper functions for file type operations
 */

// Get allowed file types message for error display
export const getAllowedFileTypesMessage = () => {
  const typeNames = Object.values(PRESCRIPTION_FILE_CONFIG.ALLOWED_FILE_TYPES)
    .map(type => type.displayName);
  return `Only ${typeNames.join(", ")} files are allowed.`;
};

// Validate if a file type is allowed
export const isFileTypeAllowed = (fileType) => {
  return Object.keys(PRESCRIPTION_FILE_CONFIG.ALLOWED_FILE_TYPES).includes(fileType);
};

// Generate accept attribute for HTML file input
export const getFileInputAcceptAttribute = () => {
  const mimeTypes = Object.keys(PRESCRIPTION_FILE_CONFIG.ALLOWED_FILE_TYPES);
  const extensions = Object.values(PRESCRIPTION_FILE_CONFIG.ALLOWED_FILE_TYPES)
    .flatMap(type => type.extensions)
    .map(ext => `.${ext}`);
  return [...mimeTypes, ...extensions].join(",");
};

// Get file extension from URL or filename
export const getFileExtension = (url) => {
  try {
    // Extract filename from URL or path
    const filename = url.split("/").pop() || url;
    // Get extension from filename
    const extension = filename.split(".").pop()?.toLowerCase();
    return extension || "jpg"; // Default fallback
  } catch (error) {
    return "jpg"; // Default fallback
  }
};

// Get MIME type from file extension
export const getMimeTypeFromExtension = (extension) => {
  const extensionToMimeType = {};
  
  // Build reverse lookup from the config
  Object.entries(PRESCRIPTION_FILE_CONFIG.ALLOWED_FILE_TYPES).forEach(([mimeType, config]) => {
    config.extensions.forEach(ext => {
      extensionToMimeType[ext] = mimeType;
    });
  });
  
  return extensionToMimeType[extension.toLowerCase()] || "image/jpeg";
};

// Get human-readable file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Get appropriate file extension from URL with intelligent detection
export const getDownloadFilename = (url, fallbackTitle) => {
  try {
    // First try to extract filename from URL
    const urlParts = url.split('/');
    const lastPart = urlParts[urlParts.length - 1];
    
    // Check if the last part contains a file extension
    if (lastPart && lastPart.includes('.')) {
      return decodeURIComponent(lastPart.split('?')[0]); // Remove query params
    }
    
    // If no filename in URL, use title with appropriate extension
    if (fallbackTitle) {
      // Try to determine extension from URL content-type or path
      const extension = url.toLowerCase().includes('pdf') ? '.pdf' : 
                       url.toLowerCase().includes('png') ? '.png' :
                       url.toLowerCase().includes('jpg') || url.toLowerCase().includes('jpeg') ? '.jpg' :
                       url.toLowerCase().includes('gif') ? '.gif' :
                       url.toLowerCase().includes('webp') ? '.webp' :
                       '.png'; // Default fallback
      return `${fallbackTitle.replace(/[^a-zA-Z0-9]/g, '_')}${extension}`;
    }
    
    // Ultimate fallback
    return `prescription_${Date.now()}.png`;
  } catch (error) {
    return `prescription_${Date.now()}.png`;
  }
};

export default PRESCRIPTION_FILE_CONFIG;
